import API_SERVICE from '@Service/apiService';
import { DocumentStatus } from '@Constants/status';
import { appendBuffer, arrayBufferToBase64, b64toBlob } from '@Utils/arrayUtils';
import { clearSubmissionIDs, useOriginalValue, cleanMetadataValue } from '@Utils/documentUtils';
import extractFilename, { getFileExtension } from '@Utils/fileUtils';
import {
    STORAGE_TYPE,
    setInStorage,
    getFromStorage,
    removeFromStorage
} from '@Service/storageService';
import { LOG_TYPE, addLog } from '@Service/loggingService';
import {
    DOCUMENT_IMPORT,
    DOCUMENT_METADATA,
    DOCUMENT_MARKUP,
    DOCUMENT_SCORECARD,
    DOCUMENT_VIEWS,
    NOTIFICATION,
    AUTHENTICATION,
    NOTIFICATION_HANDLER,
    DOCUMENT_ANIMATION,
    FILTERS,
    MIX_PANEL
} from '@Constants/types';
import store from '@Store';
import mixpanelEvents from '@Mixpanel';
import getClauseLibrary from '@Actions/clauseActions';
import { calculateOverflow } from '@Utils/commonUtils';

/* global Office */
let filename;
let jobStatusCheckCounter = 0;
let WholeFile = new Uint8Array(0);

// get current file name
export function getFileName() {
    try {
        return new Promise(resolve => {
            Office.context.document.getFilePropertiesAsync(asyncResult =>
                resolve(extractFilename(asyncResult.value.url))
            );
        });
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'documentActions.getFileName', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
        return null;
    }
}

export function getFileSize() {
    try {
        Office.context.document.getFileAsync(Office.FileType.Text, { sliceSize: 10000 }, result => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                const myFile = result.value;
                myFile.closeAsync(closeResult => {
                    if (closeResult.status === Office.AsyncResultStatus.Succeeded) {
                        store.dispatch({
                            type: NOTIFICATION.FILE_SIZE,
                            payload: myFile.size
                        });
                    } else {
                        store.dispatch({ type: NOTIFICATION.FILE_SIZE, payload: -1 });
                    }
                });
            } else if (result.status !== Office.AsyncResultStatus.Succeeded) {
                store.dispatch({ type: NOTIFICATION.FILE_SIZE, payload: -1 });
            }
        });
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'documentActions.getFileSize', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

// Collect file pieces
export function submitFile(digiAppId, digiAppName, currentPage) {
    try {
        clearSubmissionIDs();
        setInStorage('digAppId', digiAppId, STORAGE_TYPE.PROPERTY_BAG);
        setInStorage('digAppName', digiAppName, STORAGE_TYPE.PROPERTY_BAG);
        setInStorage('sleProcessStarted', false, STORAGE_TYPE.PROPERTY_BAG);

        store.dispatch({
            type: MIX_PANEL.MIXPANEL_DATA_SCORECARD_STEP_TIME,
            payload: 'initialize'
        });
        store.dispatch({ type: FILTERS.RESET_FILTERS });
        store.dispatch({ type: DOCUMENT_SCORECARD.CLEAR_SCORECARD });
        store.dispatch({ type: DOCUMENT_IMPORT.IMPORT_REQUEST });
        store.dispatch(startAnimation());
        calculateOverflow('');
        store.dispatch({ type: DOCUMENT_SCORECARD.DISABLE_SCORECARD_POLLING });
        store.dispatch(getClauseLibrary());

        const rootReducer = store.getState();
        const { document } = rootReducer;
        const { DocumentStatus: status } = document;
        if (status === 'Not submitted') {
            // abort
            return;
        }

        Office.context.document.getFilePropertiesAsync(asyncResult => {
            filename = extractFilename(asyncResult.value.url);
            const extension = getFileExtension(filename);
            setInStorage('filename', filename, STORAGE_TYPE.LOCAL_STORAGE);
            if (filename === '') {
                // dispatch the error message detailing the reason for the import failure
                store.dispatch({
                    type: DOCUMENT_IMPORT.IMPORT_ERROR
                });
                store.dispatch({
                    type: NOTIFICATION_HANDLER.WARNING,
                    payload:
                        'There was a problem with analyzing the document, check if it’s been saved then try again.'
                });

                return;
            }

            WholeFile = new Uint8Array(0);
            let OfficeFileType = Office.FileType.Compressed;
            // Set OfficeFileType if file ext is pdf
            if (extension.toLowerCase() === 'pdf') {
                OfficeFileType = Office.FileType.Pdf;
            }
            Office.context.document.getFileAsync(OfficeFileType, { sliceSize: 10000 }, result => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                    const myFile = result.value;
                    const state = {
                        file: myFile,
                        counter: 0,
                        sliceCount: myFile.sliceCount
                    };

                    const docAnalysisEventProperties = {
                        'Logic App Name': digiAppName,
                        'Document Size': `${myFile.size} bytes`,
                        'Analyzer Display Mode': currentPage
                    };
                    mixpanelEvents.docAnalysis(docAnalysisEventProperties);

                    updateStatus(`Getting file of ${myFile.size} bytes`);
                    getSlice(state);
                    return true;
                }
                updateStatus(result.status);
                store.dispatch({
                    type: NOTIFICATION_HANDLER.DISPLAY_ERROR,
                    payload:
                        'Failed to submit the document, please reload the add-in and try again.'
                });
                return false;
            });
        });
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'documentActions.submitFile', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

// Upload the document to SEAL
function UploadDoc(objectData) {
    try {
        API_SERVICE.refresh();
        API_SERVICE.setContentType('multipart/form-data');

        if (API_SERVICE === null) {
            updateStatus('Seal url missing!');
            return;
        }

        const rootReducer = store.getState();
        const { document } = rootReducer;
        const { DocumentStatus: status } = document;
        if (status === 'Not submitted') {
            // abort
            return;
        }

        const sealUrl = getFromStorage('seal_url', STORAGE_TYPE.LOCAL_STORAGE);
        const blob = b64toBlob(objectData.Body, objectData.contentType);
        const bodyFormData = new FormData();
        bodyFormData.append('file', blob, filename);
        bodyFormData.set('reference', objectData.reference);
        API_SERVICE.SEAL_API({
            method: 'POST',
            url: '/seal-ws/v5/documents',
            data: bodyFormData
        })
            .then(res => {
                UpdateDocumentProperties(res.data, sealUrl, 'Pending');
            })
            .catch(error => {
                addLog(LOG_TYPE.ERROR, 'documentActions.UploadDoc', error);
                store.dispatch({
                    type: DOCUMENT_IMPORT.IMPORT_FAILURE
                });

                handelErrorMessage(error);
            });
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'documentActions.UploadDoc', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

function UpdateDocumentProperties(data, sealUrl, status) {
    try {
        Office.context.document.getFileAsync(Office.FileType.Text, { sliceSize: 10000 }, result => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                const myFile = result.value;
                myFile.closeAsync(closeResult => {
                    if (closeResult.status === Office.AsyncResultStatus.Succeeded) {
                        if (data.documentId && data.jobId) {
                            store.dispatch({
                                type: MIX_PANEL.MIXPANEL_DATA_SCORECARD_STEP_TIME,
                                payload: 'analyzing'
                            });
                            mixpanelEvents.initializingstepcomplete();
                            setInStorage(
                                'sealAtnDocumentId',
                                data.documentId,
                                STORAGE_TYPE.PROPERTY_BAG
                            );

                            setInStorage('sealUrl', sealUrl, STORAGE_TYPE.PROPERTY_BAG);
                            setInStorage('jobId', data.jobId, STORAGE_TYPE.PROPERTY_BAG);
                            setInStorage('jobStatus', 'Pending', STORAGE_TYPE.PROPERTY_BAG);
                            setInStorage('status', status, STORAGE_TYPE.PROPERTY_BAG);
                            setInStorage(
                                'lastSubmissionFileSize',
                                myFile.size,
                                STORAGE_TYPE.PROPERTY_BAG
                            );

                            store.dispatch({ type: DOCUMENT_IMPORT.IMPORT_SUCCESS, payload: data });
                            checkJobStatus(data.jobId);
                        } else {
                            store.dispatch({
                                type: DOCUMENT_IMPORT.IMPORT_ERROR
                            });
                            store.dispatch({
                                type: NOTIFICATION_HANDLER.ERROR,
                                payload:
                                    'There was a problem when submitting the document, please try again. Contact your system administrator if this issue persists.'
                            });
                        }
                    } else {
                        store.dispatch({
                            type: DOCUMENT_IMPORT.IMPORT_ERROR
                        });
                        store.dispatch({
                            type: NOTIFICATION_HANDLER.ERROR,
                            payload:
                                'There was a problem uploading the document to Seal, please try again. Contact your system administrator if this issue persists.'
                        });
                    }
                });
            } else if (result.status !== Office.AsyncResultStatus.Succeeded) {
                store.dispatch({
                    type: DOCUMENT_IMPORT.IMPORT_ERROR
                });
                store.dispatch({
                    type: NOTIFICATION_HANDLER.ERROR,
                    payload:
                        'There was a problem calculating the file size of the document, please try again. Contact your system administrator if this issue persists.'
                });
            }
        });
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'documentActions.UpdateDocumentProperties', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

// Check if SEAL job is finished.  If the job gas failed, extract the error details and dispatch the error message
function checkJobStatus(jobId) {
    try {
        if (!jobId) {
            updateStatus('Unknown Job ID');
            return;
        }

        jobStatusCheckCounter += 1;
        updateStatus(`Checking SEAL job status, Try number : ${jobStatusCheckCounter}`);

        API_SERVICE.refresh();
        API_SERVICE.setContentType('application/json');
        API_SERVICE.SEAL_API.get(
            `/seal-ws/v5/jobs/import-filesystem/${jobId}?expand=failedDocumentsInfo`
        )
            .then(res => {
                let trace = `startedAt=${res.data.startedAt}`;
                trace = `${trace},endedAt=${res.data.endedAt}`;
                trace = `${trace},percentage=${res.data.percentage}`;
                trace = `${trace},finished=${res.data.finished}`;
                trace = `${trace},totalFiles=${res.data.totalFiles}`;
                trace = `${trace},totalDirectories=${res.data.totalDirectories}`;
                trace = `${trace},extractedDocuments=${res.data.extractedDocuments}`;
                trace = `${trace},totalFailedDocuments=${res.data.totalFailedDocuments}`;

                addLog(LOG_TYPE.INFO, 'documentActions.checkJobStatus', trace);
                // We are only sending a single document in the processing batch, therefore a single failure message in the returned json indicates an import failure
                const successfullyExtracted = !res.data.totalFailedDocuments > 0;

                // Normally, when jobStatus.finished==true the batch job has completed (irrespective of the success/failure of the document processing)
                // Note: Documents that have been sent for re-processing are in the unusual state of Finsished=false whilst PercentageComplete=100%
                //       "Re-process" is a separate API call that is made to generated the exported docx is absent, instructing the backend to re-generate this file
                //       Currently the Office.js ATN does not have this re-processing capability, but other ATNs do, and therefore this state must be handled going forward
                if (
                    res.data.finished === true ||
                    (res.data.finished === false && res.data.percentage === '100')
                ) {
                    // batch job has completed, now check to see how successful the import was
                    if (successfullyExtracted) {
                        // Success! - The document in the processing batch has been processed successfully by the seal backend.  Now go fetch the metadata
                        setInStorage('jobStatus', 'Success', STORAGE_TYPE.PROPERTY_BAG);
                        removeFromStorage('jobErrorMessage', STORAGE_TYPE.PROPERTY_BAG);

                        store.dispatch({
                            type: DOCUMENT_SCORECARD.ENABLE_SCORECARD_POLLING
                        });

                        fetchMetaData(false);
                    } else {
                        abortAnimation();

                        // failedDocumentsInfo.items is an array of errors.  This ATN is only submitting a single document per batch resulting in
                        // just one error being returned (if any).  Therefore the error information is always in the zero'th position in the error array
                        const failureType = res.data.failedDocumentsInfo.items[0].type;
                        const failureMessage = res.data.failedDocumentsInfo.items[0].message;
                        const errorMessage = `${failureType}: ${failureMessage}`;

                        setInStorage('jobStatus', 'Failed', STORAGE_TYPE.PROPERTY_BAG);
                        setInStorage('jobErrorMessage', errorMessage, STORAGE_TYPE.PROPERTY_BAG);

                        // dispatch the error message detailing the reason for the import failure
                        store.dispatch({
                            type: DOCUMENT_IMPORT.IMPORT_ERROR
                        });

                        addLog(LOG_TYPE.ERROR, 'documentActions.checkJobStatus', errorMessage);

                        // Capture the following Seal error, and cancel the submission
                        // EXTRACTION_FAILED: Could not extract data: Unexpected RuntimeException from seal.extraction.tika.parser.office.SealOOXMLParser@3daf6349
                        if (errorMessage && errorMessage.startsWith('EXTRACTION_FAILED')) {
                            if (errorMessage.indexOf('extractions is failed') > -1) {
                                store.dispatch({
                                    type: NOTIFICATION_HANDLER.WARNING,
                                    payload:
                                        'There was a problem with analyzing the document, check if it’s a contract then try sending it again.'
                                });
                            } else {
                                store.dispatch({
                                    type: NOTIFICATION_HANDLER.ERROR,
                                    payload: errorMessage
                                });
                            }

                            return;
                        }

                        if (errorMessage && errorMessage.startsWith('NOT_A_CONTRACT')) {
                            store.dispatch({
                                type: NOTIFICATION_HANDLER.WARNING,
                                payload:
                                    'There was a problem with analyzing the document, check if it’s a contract then try sending it again.'
                            });

                            return;
                        }

                        store.dispatch({
                            type: NOTIFICATION_HANDLER.ERROR,
                            payload: errorMessage
                        });
                    }
                } else if (
                    !store.getState().documentAnimation.cancel &&
                    !store.getState().documentAnimation.abort
                ) {
                    // batch not complete, keep polling for a change in status only when the animation is working

                    setTimeout(() => checkJobStatus(jobId), 3000);
                }
            })
            .catch(error => {
                addLog(LOG_TYPE.ERROR, 'documentActions.checkJobStatus', error);
                setInStorage('jobStatus', 'Failed', STORAGE_TYPE.PROPERTY_BAG);

                // Don't raise jobId not found errors because this is normal when switching systems
                if (error.response && error.response.data) {
                    const { message } = error.response.data;
                    if (message && message.length === 70) {
                        if (
                            message.startsWith('Job status for id') &&
                            message.endsWith('is not found.')
                        ) {
                            store.dispatch({
                                type: DOCUMENT_METADATA.FETCH_METADATA_CANCELLED
                            });
                            return;
                        }
                    }
                }

                // unexpected exception, dispatch error details
                store.dispatch({
                    type: DOCUMENT_METADATA.FETCH_METADATA_FAILURE
                });

                handelErrorMessage(error);
            });
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'documentActions.checkJobStatus', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

export function refreshMetaData() {
    try {
        fetchMetaData(true);
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'documentActions.refreshMetaData', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

function fetchLabelsPromise(documentId) {
    return new Promise(resolve => {
        API_SERVICE.SEAL_API({
            method: 'GET',
            url: `seal-ws/v5/contracts/${documentId}?expand=metadata,docxParagraphOffsets`
        })
            .then(res => {
                const { metadata, importedAt } = res.data;
                const { items: metadataItems } = metadata;
                metadata.items = prepMetadataValues(
                    metadataItems,
                    res.data.docxParagraphOffsets.items
                );
                store.dispatch({
                    type: DOCUMENT_METADATA.FETCH_METADATA_SUCCESS,
                    payload: { metadata, importedAt }
                });
                setInStorage(
                    'sealAtnDocumentStatus',
                    DocumentStatus.PROCESSED,
                    STORAGE_TYPE.PROPERTY_BAG
                );

                calculateOverflow('');
                return resolve();
            })
            .catch(error => {
                addLog(LOG_TYPE.ERROR, 'documentActions.fetchMetaData', error);
                // If we receive a 404 not found error whilst the job status is still pending then keep checking the jobStatus for an update
                if (error.response) {
                    if (error.response.status === 404) {
                        // Repeated calls to getFromStorage(PROPERTY_BAG) can result in the null data being returned
                        // The exact circumstances are not fully understood.  Therefore we we need to handle
                        // the scenario where the jobStatus and jobId are incorrectly returned as null
                        const jobStatus = getFromStorage('jobStatus', STORAGE_TYPE.PROPERTY_BAG);
                        const jobId = getFromStorage('jobId', STORAGE_TYPE.PROPERTY_BAG);

                        if (jobStatus && jobId) {
                            if (jobStatus === 'Pending') {
                                setTimeout(() => checkJobStatus(jobId), 3000);
                                return resolve();
                            }

                            if (jobStatus === 'Success') {
                                const { message } = error.response.data;
                                if (message && message.length === 69) {
                                    if (
                                        message.startsWith('Contract with id') &&
                                        message.endsWith('not found')
                                    ) {
                                        store.dispatch({
                                            type: DOCUMENT_METADATA.FETCH_METADATA_CANCELLED
                                        });
                                        return resolve();
                                    }
                                }
                                return resolve();
                            }
                        } else {
                            store.dispatch({
                                type: NOTIFICATION_HANDLER.ERROR,
                                payload:
                                    'There was a problem with analyzing the document, please try sending it again.'
                            });

                            return resolve();
                        }
                    }
                }

                store.dispatch({
                    type: DOCUMENT_METADATA.FETCH_METADATA_FAILURE,
                    payload: error
                });

                handelErrorMessage(error);
                return resolve();
            });
    });
}

function fetchLabels(documentId) {
    API_SERVICE.SEAL_API({
        method: 'GET',
        url: `seal-ws/v5/contracts/${documentId}?expand=metadata,docxParagraphOffsets`
    })
        .then(res => {
            const { metadata, importedAt } = res.data;
            const { items: metadataItems } = metadata;
            metadata.items = prepMetadataValues(metadataItems, res.data.docxParagraphOffsets.items);
            store.dispatch({
                type: DOCUMENT_METADATA.FETCH_METADATA_SUCCESS,
                payload: { metadata, importedAt }
            });
            setInStorage(
                'sealAtnDocumentStatus',
                DocumentStatus.PROCESSED,
                STORAGE_TYPE.PROPERTY_BAG
            );
            calculateOverflow('');
        })
        .catch(error => {
            addLog(LOG_TYPE.ERROR, 'documentActions.fetchMetaData', error);
            // If we receive a 404 not found error whilst the job status is still pending then keep checking the jobStatus for an update
            if (error.response) {
                if (error.response.status === 404) {
                    // Repeated calls to getFromStorage(PROPERTY_BAG) can result in the null data being returned
                    // The exact circumstances are not fully understood.  Therefore we we need to handle
                    // the scenario where the jobStatus and jobId are incorrectly returned as null
                    const jobStatus = getFromStorage('jobStatus', STORAGE_TYPE.PROPERTY_BAG);
                    const jobId = getFromStorage('jobId', STORAGE_TYPE.PROPERTY_BAG);

                    if (jobStatus && jobId) {
                        if (jobStatus === 'Pending') {
                            setTimeout(() => checkJobStatus(jobId), 3000);
                            return;
                        }

                        if (jobStatus === 'Success') {
                            const { message } = error.response.data;
                            if (message && message.length === 69) {
                                if (
                                    message.startsWith('Contract with id') &&
                                    message.endsWith('not found')
                                ) {
                                    store.dispatch({
                                        type: DOCUMENT_METADATA.FETCH_METADATA_CANCELLED
                                    });
                                    return;
                                }
                            }
                            return;
                        }
                    } else {
                        store.dispatch({
                            type: NOTIFICATION_HANDLER.ERROR,
                            payload:
                                'There was a problem with analyzing the document, please try sending it again.'
                        });

                        return;
                    }
                }
            }

            store.dispatch({
                type: DOCUMENT_METADATA.FETCH_METADATA_FAILURE,
                payload: error
            });

            handelErrorMessage(error);
        });
}

export function fetchMetaDataPromise() {
    const refresh = true;
    return new Promise(resolve => {
        try {
            const documentSealUrl = getFromStorage('sealUrl', STORAGE_TYPE.PROPERTY_BAG);
            const targetSystemSealUrl = getFromStorage('seal_url', STORAGE_TYPE.LOCAL_STORAGE);
            let isSubmittedOnDiffBackend = false;

            if (documentSealUrl) {
                isSubmittedOnDiffBackend =
                    documentSealUrl !== '' && documentSealUrl !== targetSystemSealUrl;
            }

            const jobStatus = getFromStorage('jobStatus', STORAGE_TYPE.PROPERTY_BAG);
            if (jobStatus && jobStatus === 'Failed') {
                if (isSubmittedOnDiffBackend) {
                    // if this submission failure occured on a different backend system then don't display the error, just set the state to "Not Submitted"
                    store.dispatch({
                        type: DOCUMENT_METADATA.FETCH_METADATA_CANCELLED
                    });
                } else {
                    // last submission error was generated from this system, so surface that last submission error to the end-user
                    const jobErrorMessage = getFromStorage(
                        'jobErrorMessage',
                        STORAGE_TYPE.PROPERTY_BAG
                    );

                    store.dispatch({
                        type: DOCUMENT_METADATA.FETCH_METADATA_ERROR
                    });

                    addLog(LOG_TYPE.ERROR, 'documentActions.fetchMetaData', jobErrorMessage);

                    // Capture the following Seal error, and cancel the submission
                    // EXTRACTION_FAILED: Could not extract data: Unexpected RuntimeException from seal.extraction.tika.parser.office.SealOOXMLParser@3daf6349
                    if (jobErrorMessage.startsWith('EXTRACTION_FAILED')) {
                        if (jobErrorMessage.indexOf('extractions is failed') > -1) {
                            store.dispatch({
                                type: NOTIFICATION_HANDLER.WARNING,
                                payload:
                                    'There was a problem with analyzing the document, check if it’s a contract then try sending it again.'
                            });
                        } else {
                            store.dispatch({
                                type: NOTIFICATION_HANDLER.ERROR,
                                payload: jobErrorMessage
                            });
                        }

                        store.dispatch({
                            type: DOCUMENT_METADATA.FETCH_METADATA_CANCELLED
                        });

                        return;
                    }

                    store.dispatch({
                        type: NOTIFICATION_HANDLER.ERROR,
                        payload: jobErrorMessage
                    });
                }

                return resolve();
            }

            if (isSubmittedOnDiffBackend) {
                store.dispatch({
                    type: DOCUMENT_METADATA.FETCH_METADATA_CANCELLED
                });

                return resolve();
            }

            API_SERVICE.refresh();
            API_SERVICE.setContentType('application/json');
            const documentId = getFromStorage('sealAtnDocumentId', STORAGE_TYPE.PROPERTY_BAG);

            if (documentId && documentId.length > 0) {
                store.dispatch({
                    type: DOCUMENT_METADATA.FETCH_METADATA_REQUEST,
                    payload: { refresh }
                });
                let isExtracted = false;
                let isAnalyticsThere = true;
                let analyticsCheck = 0;
                let intervalId = setInterval(() => {
                    if (isAnalyticsThere) {
                        if (isExtracted) {
                            clearInterval(intervalId);
                            fetchLabelsPromise(documentId).then(resolve());
                        } else {
                            API_SERVICE.SEAL_API({
                                method: 'GET',
                                url: `seal-ws/v5/contracts/${documentId}/status`
                            })
                                .then(res => {
                                    const object = res.data.items.filter(
                                        item => item.statusType === 'Analytics'
                                    );
                                    if (object.length !== 0) {
                                        const extractedObject = object.filter(
                                            item => item.name === 'Extracted'
                                        );
                                        isExtracted =
                                            extractedObject.length !== 0 &&
                                            extractedObject[0].value === true;
                                    } else {
                                        if (analyticsCheck === 10) isAnalyticsThere = false;
                                        analyticsCheck++;
                                    }
                                })
                                .catch(err => {
                                    if (err.toString().includes('403')) {
                                        addLog(
                                            LOG_TYPE.ERROR,
                                            'documentActions.fetchMetaData',
                                            'Missing permissions: Contract > Read'
                                        );
                                        return resolve();
                                    }
                                });
                        }
                    } else {
                        clearInterval(intervalId);
                        fetchLabelsPromise(documentId).then(resolve());
                    }
                }, 1000);
            } else {
                return resolve();
            }
        } catch (err) {
            addLog(LOG_TYPE.ERROR, 'documentActions.fetchMetaData', err);
            store.dispatch({
                type: NOTIFICATION_HANDLER.ERROR,
                payload: err
            });

            return resolve();
        }
    });
}

// Fetch document's metadata from SEAL
export function fetchMetaData(refresh) {
    try {
        const documentSealUrl = getFromStorage('sealUrl', STORAGE_TYPE.PROPERTY_BAG);
        const targetSystemSealUrl = getFromStorage('seal_url', STORAGE_TYPE.LOCAL_STORAGE);
        let isSubmittedOnDiffBackend = false;

        if (documentSealUrl) {
            isSubmittedOnDiffBackend =
                documentSealUrl !== '' && documentSealUrl !== targetSystemSealUrl;
        }

        const jobStatus = getFromStorage('jobStatus', STORAGE_TYPE.PROPERTY_BAG);
        if (jobStatus && jobStatus === 'Failed') {
            if (isSubmittedOnDiffBackend) {
                // if this submission failure occured on a different backend system then don't display the error, just set the state to "Not Submitted"
                store.dispatch({
                    type: DOCUMENT_METADATA.FETCH_METADATA_CANCELLED
                });
            } else {
                // last submission error was generated from this system, so surface that last submission error to the end-user
                const jobErrorMessage = getFromStorage(
                    'jobErrorMessage',
                    STORAGE_TYPE.PROPERTY_BAG
                );
                store.dispatch({
                    type: DOCUMENT_METADATA.FETCH_METADATA_ERROR
                });

                addLog(LOG_TYPE.ERROR, 'documentActions.fetchMetaData', jobErrorMessage);

                // Capture the following Seal error, and cancel the submission
                // EXTRACTION_FAILED: Could not extract data: Unexpected RuntimeException from seal.extraction.tika.parser.office.SealOOXMLParser@3daf6349
                if (jobErrorMessage.startsWith('EXTRACTION_FAILED')) {
                    if (jobErrorMessage.indexOf('extractions is failed') > -1) {
                        store.dispatch({
                            type: NOTIFICATION_HANDLER.WARNING,
                            payload:
                                'There was a problem with analyzing the document, check if it’s a contract then try sending it again.'
                        });
                    } else {
                        store.dispatch({
                            type: NOTIFICATION_HANDLER.ERROR,
                            payload: jobErrorMessage
                        });
                    }

                    store.dispatch({
                        type: DOCUMENT_METADATA.FETCH_METADATA_CANCELLED
                    });

                    return;
                }

                store.dispatch({
                    type: NOTIFICATION_HANDLER.ERROR,
                    payload: jobErrorMessage
                });
            }

            return;
        }

        if (isSubmittedOnDiffBackend) {
            store.dispatch({
                type: DOCUMENT_METADATA.FETCH_METADATA_CANCELLED
            });

            return;
        }

        API_SERVICE.refresh();
        API_SERVICE.setContentType('application/json');
        const documentId = getFromStorage('sealAtnDocumentId', STORAGE_TYPE.PROPERTY_BAG);

        if (documentId && documentId.length > 0) {
            store.dispatch({
                type: DOCUMENT_METADATA.FETCH_METADATA_REQUEST,
                payload: { refresh }
            });
            let isExtracted = false;
            let isAnalyticsThere = true;
            let analyticsCheck = 0;
            let intervalId = setInterval(() => {
                if (isAnalyticsThere) {
                    if (isExtracted) {
                        clearInterval(intervalId);
                        fetchLabels(documentId);
                    } else {
                        API_SERVICE.SEAL_API({
                            method: 'GET',
                            url: `seal-ws/v5/contracts/${documentId}/status`
                        }).then(res => {
                            const object = res.data.items.filter(
                                item => item.statusType === 'Analytics'
                            );
                            if (object.length !== 0) {
                                const extractedObject = object.filter(
                                    item => item.name === 'Extracted'
                                );
                                isExtracted =
                                    extractedObject.length !== 0 &&
                                    extractedObject[0].value === true;
                            } else {
                                if (analyticsCheck === 10) isAnalyticsThere = false;
                                analyticsCheck++;
                            }
                        });
                    }
                } else {
                    clearInterval(intervalId);
                    fetchLabels(documentId);
                }
            }, 1000);
        }
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'documentActions.fetchMetaData', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

function prepMetadataValues(metadataItems, paragraphOffsets) {
    try {
        metadataItems = removeMetadata(metadataItems);
        metadataItems.forEach(metadataItem => {
            metadataItem.values.forEach(valueItem => {
                valueItem.paragraphID = getParagraphIndex(valueItem, paragraphOffsets);
                valueItem.nonStandard = checkNonStanderdItems(valueItem);
                valueItem.value = useOriginalValue(valueItem);
                valueItem.value = cleanMetadataValue(
                    valueItem.value2 ? valueItem.value2 : valueItem.value
                );

                valueItem.navigableFlag = isNavigable(metadataItem, valueItem);
            });
        });
        return metadataItems;
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'documentActions.prepMetadataValues', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
        return metadataItems;
    }
}

function isNavigable(metadataItem, valueItem) {
    try {
        let navigableFlag = false;

        // if the annotation has start/end offsets then its likely to be navigable (with the following exceptions below)
        if (valueItem.startOffset && valueItem.endOffset) {
            navigableFlag = true;
        }

        // annotations generated from rules that result in true/false answers should have the click-to-navigate disabled
        if (
            valueItem.value.toString().toLowerCase() === 'true' ||
            valueItem.value.toString().toLowerCase() === 'false'
        ) {
            navigableFlag = false;
        }

        // Contract Class is a derived field and will not be navigable
        if (metadataItem.name === 'ContractClass' || metadataItem.name === 'ContractType') {
            navigableFlag = false;
        }

        return navigableFlag;
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'documentActions.isNavigable', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
        return false;
    }
}

function removeMetadata(metadataItems) {
    return metadataItems.filter(item => !['AmbiguousData', 'TableData'].includes(item.name));
}

// Computing the index of the paragraph from the global offset & document's paragraphs offset
function getParagraphIndex(valueItem, paragraphOffsets) {
    try {
        let paragraphIndex = '';
        const { value } = valueItem;
        const globalOffset = valueItem.startOffset;
        if (value && globalOffset && paragraphOffsets) {
            Object.keys(paragraphOffsets).forEach((element, index, arr) => {
                if (
                    (globalOffset >= element && arr[index + 1] && globalOffset < arr[index + 1]) ||
                    (globalOffset >= element && !arr[index + 1])
                ) {
                    paragraphIndex = paragraphOffsets[element];
                }
            });
        }
        return paragraphIndex;
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'documentActions.getParagraphIndex', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
        return '';
    }
}

function checkNonStanderdItems(valueItem) {
    try {
        let nonStandard = false;
        valueItem.attributes.forEach(element => {
            if (element.name === 'scaType' && element.value === 'Non-Standard') {
                nonStandard = true;
            }
        });
        return nonStandard;
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'documentActions.checkNonStanderdItems', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
        return false;
    }
}

// Helper methods for collecting file pieces
function getSlice(state) {
    try {
        state.file.getSliceAsync(state.counter, result => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                sendSlice(result.value, state);
            } else {
                updateStatus(result.status);
                state.file.closeAsync();
                store.dispatch({
                    type: NOTIFICATION_HANDLER.DISPLAY_ERROR,
                    payload: 'Failed to read the document, please reload the add-in and try again.'
                });
            }
        });
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'documentActions.getSlice', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

function sendSlice(slice, state) {
    try {
        const { data } = slice;

        // If the slice contains data, create an HTTP request.
        if (data) {
            // Encode the slice data, a byte array, as a Base64 string.
            // NOTE: The implementation of myEncodeBase64(input) function isn't
            // included with this example. For information about Base64 encoding with
            // JavaScript, see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Base64_encoding_and_decoding.
            WholeFile = appendBuffer(WholeFile, data);
            state.counter += 1;

            if (state.counter < state.sliceCount) {
                getSlice(state);
            } else {
                closeFile(state);
            }
        }
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'documentActions.sendSlice', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

function closeFile(state) {
    try {
        const extension = getFileExtension(getFromStorage('filename', STORAGE_TYPE.LOCAL_STORAGE));
        // Close the file when you're done with it.
        state.file.closeAsync(result => {
            // If the result returns as a success, the
            // file has been successfully closed.
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                const base64str = arrayBufferToBase64(WholeFile);

                if (base64str.substring(0, 2) !== 'UE' && extension.toLowerCase() !== 'pdf') {
                    addLog(
                        LOG_TYPE.ERROR,
                        'documentActions.closeFile',
                        'Document content did not start with "UE", therefore the call to fetch the document has failed'
                    );
                    store.dispatch({
                        type: NOTIFICATION_HANDLER.WARNING,
                        payload:
                            'Failed to read document.  Please retry.  If retrying fails, create a copy of this document and send this copy instead.'
                    });
                    abortAnimation();
                    return;
                }

                const objectData = {
                    Body: base64str,
                    ContentType:
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    reference: 'WordAddin'
                };
                UploadDoc(objectData);
            } else {
                updateStatus("File couldn't be closed.");
                store.dispatch({ type: DOCUMENT_IMPORT.IMPORT_FAILURE });
                abortAnimation();
            }
        });
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'documentActions.closeFile', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

export function getSealViewsPromise(result) {
    // eslint-disable-next-line
    const promise = new Promise((resolve, reject) => {
        try {
            store.dispatch({ type: DOCUMENT_VIEWS.CLEAR_VIEWS });
            API_SERVICE.refresh();
            API_SERVICE.setContentType('application/json');
            API_SERVICE.SEAL_API.get('/seal-ws/internal/userviews')
                .then(res => {
                    addLog(LOG_TYPE.INFO, 'documentActions.getSealViewsPromise', res.data);
                    store.dispatch({
                        type: DOCUMENT_VIEWS.DOCUMENT_VIEWS_SUCCESS,
                        payload: res.data.views
                    });
                    const userViews = getAllUserAccessibleLabels({ ...res.data.views });
                    store.dispatch({
                        type: AUTHENTICATION.UPDATE_USERS_USERVIEWS,
                        payload: userViews
                    });

                    resolve({ res, ...result });
                })
                .catch(err => {
                    if (err.toString().includes('status code 403')) {
                        store.dispatch({
                            type: DOCUMENT_VIEWS.DOCUMENT_VIEWS_SUCCESS,
                            payload: []
                        });
                        store.dispatch({
                            type: AUTHENTICATION.UPDATE_USERS_USERVIEWS,
                            payload: []
                        });

                        const res = null;
                        resolve({ res, ...result });
                        return store.dispatch({ type: DOCUMENT_VIEWS.CLEAR_VIEWS });
                    }

                    resolve({ ...result });

                    return store.dispatch({
                        type: NOTIFICATION_HANDLER.ERROR,
                        payload: err
                    });
                });
        } catch (err) {
            reject(err);
            addLog(LOG_TYPE.ERROR, 'documentActions.getSealViewsPromise', err);
            store.dispatch({
                type: NOTIFICATION_HANDLER.ERROR,
                payload: err
            });
            return null;
        }
    });
    return promise;
}

export function getSealViews() {
    try {
        API_SERVICE.refresh();
        API_SERVICE.setContentType('application/json');

        return dispatch => {
            dispatch({ type: DOCUMENT_VIEWS.DOCUMENT_VIEWS_REQUEST });
            return API_SERVICE.SEAL_API.get('/seal-ws/internal/userviews')
                .then(res => {
                    dispatch({
                        type: DOCUMENT_VIEWS.DOCUMENT_VIEWS_SUCCESS,
                        payload: res.data.views
                    });
                    const userViews = getAllUserAccessibleLabels({ ...res.data.views });
                    dispatch({
                        type: AUTHENTICATION.UPDATE_USERS_USERVIEWS,
                        payload: userViews
                    });
                })
                .catch(() => {
                    dispatch({ type: DOCUMENT_VIEWS.DOCUMENT_VIEWS_FAILURE });
                    // API_SERVICE.handleError(err);
                });
        };
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'documentActions.getSealViews', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
        return null;
    }
}

function getAllUserAccessibleLabels(views) {
    try {
        return Object.keys(views || {})
            .map(index => views[index])
            .reduce(
                (acc, c) => (c.metadataItems ? acc.concat(c.metadataItems.map(m => m.name)) : acc),
                []
            )
            .filter((c, id, arr) => arr.indexOf(c) === id);
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'documentActions.getAllUserAccessibleLabels', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
        return null;
    }
}

function updateStatus(status) {
    // eslint-disable-next-line
    console.log(status);
}

function handelErrorMessage(error) {
    addLog(LOG_TYPE.ERROR, 'documentActions.handelErrorMessage', error);
    if (error.response) {
        if (error.response.data.status === 409) {
            store.dispatch({
                type: NOTIFICATION_HANDLER.INFO,
                payload: 'A contract with this id already exists in Seal.'
            });
        } else if (error.message.includes('CORS')) {
            store.dispatch({
                type: NOTIFICATION_HANDLER.ERROR,
                payload:
                    'Seal can’t access the SSO services, please contact your system administrator.'
            });
        } else if (error.response.data.status === 502) {
            store.dispatch({
                type: NOTIFICATION_HANDLER.ERROR,
                payload: 'Bad gateway'
            });
        } else if (error.response.data.status === 404) {
            store.dispatch({
                type: NOTIFICATION_HANDLER.ERROR,
                payload: error.response.data.message
            });
        } else if (error.message.includes(403)) {
            store.dispatch({
                type: NOTIFICATION_HANDLER.WARNING,
                payload:
                    'You do not have sufficient access rights to view this. Try logging in as another user if you need to view this.'
            });
        } else {
            // Un-handled error from the following code-points:
            // 1. uploading a document
            // 2. checking for jobStatus of a submitted document
            // 3. fetching the metadata for a submitted document
            if (error.response.data.status !== 401)
                store.dispatch({
                    type: NOTIFICATION_HANDLER.WARNING,
                    payload: 'Failed to submit the document, please try again.'
                });
        }
    } else {
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: 'No error.response details'
        });
    }
}

function startAnimation() {
    return dispatch => dispatch({ type: DOCUMENT_ANIMATION.RUN_ANIMATION });
}

export function stopAnimation() {
    store.dispatch({ type: DOCUMENT_ANIMATION.STOP_ANIMATION });
}

export function resetAnimation() {
    store.dispatch({ type: DOCUMENT_ANIMATION.RESET_ANIMATION });
}

export function cancelAnimation() {
    clearSubmissionIDs();
    store.dispatch({ type: DOCUMENT_ANIMATION.CANCEL_ANIMATION });
    store.dispatch({ type: DOCUMENT_VIEWS.CLEAR_LABELS });
    store.dispatch({ type: DOCUMENT_SCORECARD.CLEAR_SCORECARD });
}

export function abortAnimation() {
    clearSubmissionIDs();
    store.dispatch({ type: DOCUMENT_ANIMATION.ABORT_ANIMATION });
    store.dispatch({ type: DOCUMENT_VIEWS.CLEAR_VIEWS });
    store.dispatch({ type: DOCUMENT_SCORECARD.CLEAR_SCORECARD });
}

/* open in new instance starts here */

// Collect file pieces
export function CopyDocumentToBase64Str(doCopy) {
    if (doCopy === false) {
        store.dispatch({
            type: DOCUMENT_MARKUP.BASE64_DATA,
            payload: ''
        });

        return;
    }

    try {
        Office.context.document.getFilePropertiesAsync(asyncResult => {
            filename = extractFilename(asyncResult.value.url);
            const extension = getFileExtension(filename);
            WholeFile = new Uint8Array(0);
            let OfficeFileType = Office.FileType.Compressed;
            // Set OfficeFileType if file ext is pdf
            if (extension.toLowerCase() === 'pdf') {
                OfficeFileType = Office.FileType.Pdf;
            }
            Office.context.document.getFileAsync(OfficeFileType, { sliceSize: 10000 }, result => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                    const myFile = result.value;
                    const state = {
                        file: myFile,
                        counter: 0,
                        sliceCount: myFile.sliceCount
                    };

                    updateStatus(`Getting file of ${myFile.size} bytes`);
                    getSlice2(state);
                    return true;
                }
                updateStatus(result.status);
                return false;
            });
        });
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'documentActions.GetFile', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

// Helper methods for collecting file pieces
function getSlice2(state) {
    try {
        state.file.getSliceAsync(state.counter, result => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                sendSlice2(result.value, state);
            } else {
                updateStatus(result.status);
            }
        });
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'documentActions.getSlice2', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

function sendSlice2(slice, state) {
    try {
        const { data } = slice;

        // If the slice contains data, create an HTTP request.
        if (data) {
            // Encode the slice data, a byte array, as a Base64 string.
            // NOTE: The implementation of myEncodeBase64(input) function isn't
            // included with this example. For information about Base64 encoding with
            // JavaScript, see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Base64_encoding_and_decoding.
            WholeFile = appendBuffer(WholeFile, data);
            state.counter += 1;

            if (state.counter < state.sliceCount) {
                getSlice2(state);
            } else {
                closeFile2(state);
            }
        }
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'documentActions.sendSlice2', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

function closeFile2(state) {
    try {
        const extension = getFileExtension(getFromStorage('filename', STORAGE_TYPE.LOCAL_STORAGE));
        // Close the file when you're done with it.
        state.file.closeAsync(result => {
            // If the result returns as a success, the
            // file has been successfully closed.
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                const base64str2 = arrayBufferToBase64(WholeFile);

                if (base64str2.substring(0, 2) !== 'UE' && extension.toLowerCase() !== 'pdf') {
                    addLog(
                        LOG_TYPE.ERROR,
                        'documentActions.closeFile',
                        'Document content did not start with "UE", therefore the call to fetch the document has failed'
                    );
                    store.dispatch({
                        type: NOTIFICATION_HANDLER.WARNING,
                        payload:
                            'Failed to read document.  Please retry.  If retrying fails, create a copy of this document and send this copy instead.'
                    });
                    // return;
                }

                store.dispatch({
                    type: DOCUMENT_MARKUP.BASE64_DATA,
                    payload: base64str2
                });
            } else {
                updateStatus("File couldn't be closed.");
            }
        });
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'documentActions.closeFile2', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

export function toggleMarkupDetails(isOpen) {
    return {
        type: DOCUMENT_MARKUP.TOGGLE_MARKUP_DETAILS,
        payload: isOpen
    };
}

/* open in new instance ends here */
